import { useLocalStorage } from "@website/hooks/local-storage";
import {
  DOM_FLIGHT_ROUTE_HISTORY_LOCAL_STORAGE_KEY,
  DOM_FLIGHT_SEARCH_HISTORY_LOCAL_STORAGE_KEY
} from "@website/ticket-ui/common/constants";
import { FormattedCity } from "@website/ticket-ui/common/types";
import { PassengerType } from "@website/ticket-ui/dom-flight-search-panel-old/types";
import { useCallback, useEffect, useState } from "react";

type HistoryItem = {
  origin: FormattedCity;
  destination: FormattedCity;
  startDateTime: string;
  endDateTime: string;
  calendarLocale: string;
  saleType: string;
  passengerNumber: PassengerType;
};
export const useHistory = (
  onChangeOrigin: (item: FormattedCity | null) => void,
  onChangeDestination: (item: FormattedCity | null) => void
) => {
  const [history, setHistory] = useState<
    Array<{ origin: FormattedCity; destination: FormattedCity }>
  >([]);
  const { addToLocalStorage, getFromLocalStorage } = useLocalStorage();

  useEffect(() => {
    const historyString = getFromLocalStorage(
      DOM_FLIGHT_ROUTE_HISTORY_LOCAL_STORAGE_KEY
    );

    const history = historyString ? JSON.parse(historyString) : [];
    setHistory(history);
  }, []);
  const addToRouteHistory = useCallback(
    (origin: FormattedCity, destination: FormattedCity) => {
      const historyString = getFromLocalStorage(
        DOM_FLIGHT_ROUTE_HISTORY_LOCAL_STORAGE_KEY
      );
      const history = historyString ? JSON.parse(historyString) : [];

      let newHistory = history.filter(
        (item: { origin: FormattedCity; destination: FormattedCity }) =>
          item.origin.iataCode !== origin.iataCode &&
          item.destination.iataCode !== destination.iataCode
      );

      const newHistoryItem = {
        origin,
        destination
      };
      newHistory = [newHistoryItem, ...newHistory].slice(0, 3);

      addToLocalStorage(
        DOM_FLIGHT_ROUTE_HISTORY_LOCAL_STORAGE_KEY,
        JSON.stringify(newHistory)
      );
      setHistory(newHistory);
    },
    []
  );

  const addToSearchHistory = useCallback(
    (
      origin: FormattedCity,
      destination: FormattedCity,
      startDateTime: string,
      endDateTime: string,
      calendarLocale: string,
      saleType: string,
      passengerNumber: PassengerType
    ) => {
      const historyString = getFromLocalStorage(
        DOM_FLIGHT_SEARCH_HISTORY_LOCAL_STORAGE_KEY
      );
      const history = historyString ? JSON.parse(historyString) : [];
      let newHistory = history.filter(
        (item: HistoryItem) =>
          !(
            item.origin.iataCode === origin.iataCode &&
            item.destination.iataCode === destination.iataCode &&
            item.startDateTime === startDateTime &&
            (item.endDateTime && endDateTime
              ? item.endDateTime === endDateTime
              : true)
          )
      );

      const newHistoryItem = {
        origin,
        destination,
        startDateTime,
        endDateTime,
        calendarLocale,
        saleType,
        passengerNumber
      };
      newHistory = [newHistoryItem, ...newHistory].slice(0, 4);

      addToLocalStorage(
        DOM_FLIGHT_SEARCH_HISTORY_LOCAL_STORAGE_KEY,
        JSON.stringify(newHistory)
      );
    },
    []
  );

  const addToHistory = useCallback(
    (
      origin: FormattedCity,
      destination: FormattedCity,
      startDateTime: string,
      endDateTime: string,
      calendarLocale: string,
      saleType: string,
      passengerNumber: PassengerType
    ) => {
      addToRouteHistory(origin, destination);
      addToSearchHistory(
        origin,
        destination,
        startDateTime,
        endDateTime,
        calendarLocale,
        saleType,
        passengerNumber
      );
    },
    []
  );

  const onSelectOriginRouteHistory = useCallback(
    (id: string) => {
      if (history) {
        let historyItem = null;
        for (const element of history) {
          const checkKey = `${element.origin.iataCode}-${element.origin.cityName}`;
          if (`${id}` === checkKey) {
            historyItem = element;
            break;
          }
        }
        if (historyItem) {
          onChangeOrigin(historyItem.origin);
        }
      }
    },
    [history]
  );
  const onSelectDestinationRouteHistory = useCallback(
    (id: string) => {
      if (history) {
        let historyItem = null;
        for (const element of history) {
          const checkKey = `${element.destination.iataCode}-${element.destination.cityName}`;
          if (`${id}` === checkKey) {
            historyItem = element;
            break;
          }
        }

        if (historyItem) {
          onChangeDestination(historyItem.destination);
        }
      }
    },
    [history]
  );

  return {
    onSelectOriginRouteHistory,
    onSelectDestinationRouteHistory,
    addToHistory,
    history
  };
};
